import Table from "react-bootstrap/Table";
import {
  GradingStep,
  GradingStepBoxCount,
  GradingStepFreeText,
  GradingStepDetailedInformation,
  GradingStepType,
  GradingStepsConfiguration,
  GradingStepConfirm,
  GradingStepChoice,
  GradingStepMultiChoice,
  GradingStepImage,
  GradingStepCount,
  GradingStepDimensions,
} from "../../../external/inventory-component/configuration/types";
import { useReadGradingAndRefurbState } from "./persist-helpers";
import { DetailedInformationSectionOption } from "../../../external/inventory-component/configuration/detailedInformation";

export interface RefurbSummaryTableComponentProps {
  refurbConfiguration: GradingStepsConfiguration;
  refurbIdentifier: string;
}

export const GradingSummaryTable = ({
  refurbConfiguration,
  refurbIdentifier,
}: RefurbSummaryTableComponentProps) => {
  if (!refurbConfiguration.steps) {
    console.error(
      "Bad refurb configuration stored! Likely the grading result was stored before we stored the steps on the configuration. " +
        "If this grading configuration is real, it will need a manual fix."
    );
    return <code>Bad refurb configuration stored!</code>;
  }

  return (
    <Table bordered={true}>
      <thead>
        <tr>
          <th>Page</th>
          <th>Field</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        {refurbConfiguration.steps
          .map((step, index) => (
            <CompletedRowComponent
              refurbIdentifier={refurbIdentifier}
              key={index}
              index={index}
              step={step}
            />
          ))
          .filter((v) => !!v)}
      </tbody>
    </Table>
  );
};

const CompletedRowComponent = ({
  refurbIdentifier,
  step,
  index: rowIndex,
}: {
  refurbIdentifier: string;
  step: GradingStep;
  index: number;
}) => {
  switch (step.stepType) {
    case GradingStepType.BoxCount:
      return (
        <BoxCountRow
          refurbIdentifier={refurbIdentifier}
          step={step}
          rowIndex={rowIndex}
        />
      );
    case GradingStepType.Count:
      return (
        <CountRow
          refurbIdentifier={refurbIdentifier}
          step={step}
          rowIndex={rowIndex}
        />
      );
    case GradingStepType.SingleChoice:
      return (
        <SingleChoiceRow
          refurbIdentifier={refurbIdentifier}
          step={step}
          rowIndex={rowIndex}
        />
      );
    case GradingStepType.MultiChoice:
      return (
        <MultiChoiceRow
          refurbIdentifier={refurbIdentifier}
          step={step}
          rowIndex={rowIndex}
        />
      );
    case GradingStepType.DetailedInformation:
      return (
        <DetailedInfoRow
          refurbIdentifier={refurbIdentifier}
          step={step}
          rowIndex={rowIndex}
        />
      );
    case GradingStepType.Dimensions:
      return (
        <DimensionsRow
          refurbIdentifier={refurbIdentifier}
          step={step}
          rowIndex={rowIndex}
        />
      );
    case GradingStepType.CatalogueCheck:
      return (
        <tr>
          <td>{rowIndex + 1}</td>
          <td>Link Check</td>
          <td>Passed</td>
        </tr>
      );
    case GradingStepType.Complete:
      return <></>;
    case GradingStepType.FreeText:
      return (
        <FreeTextRow
          refurbIdentifier={refurbIdentifier}
          step={step}
          rowIndex={rowIndex}
        />
      );
    case GradingStepType.Confirm:
      return (
        <ConfirmRow
          refurbIdentifier={refurbIdentifier}
          step={step}
          rowIndex={rowIndex}
        />
      );
    case GradingStepType.Image:
      return (
        <ImageStepSummaryRow
          refurbIdentifier={refurbIdentifier}
          step={step}
          rowIndex={rowIndex}
        />
      );
    default:
      console.error("Unknown step type", step);
      return (
        <tr>
          <td>{rowIndex + 1}</td>
          <td>
            <code>Unsupported step type {step.stepType}</code>
          </td>
          <td></td>
        </tr>
      );
  }
};

type SummaryRowProps<T> = {
  refurbIdentifier: string;
  step: T;
  rowIndex: number;
};

const BoxCountRow = ({
  refurbIdentifier,
  step,
  rowIndex,
}: SummaryRowProps<GradingStepBoxCount>) => {
  const boxCount = useReadGradingAndRefurbState<number>(
    refurbIdentifier,
    step.stepOutputKey
  );

  return (
    <tr>
      <td>{rowIndex + 1}</td>
      <td>Box Count ({step.variant})</td>
      <td>{boxCount}</td>
    </tr>
  );
};

const CountRow = ({
  refurbIdentifier,
  step,
  rowIndex,
}: SummaryRowProps<GradingStepCount>) => {
  const count = useReadGradingAndRefurbState<number>(
    refurbIdentifier,
    step.stepOutputKey
  );

  return (
    <tr>
      <td>{rowIndex + 1}</td>
      <td>{step.title}</td>
      <td>{count}</td>
    </tr>
  );
};

const DetailedInfoRow = ({
  refurbIdentifier,
  rowIndex,
  step,
}: SummaryRowProps<GradingStepDetailedInformation>) => {
  const damagesData = useReadGradingAndRefurbState<
    Record<string, Record<string, number | boolean>>
  >(refurbIdentifier, step.stepOutputKey);

  if (!damagesData) {
    let value = "Detailed Info";
    if (
      step.configuration?.sections &&
      step.configuration?.sections.length > 0
    ) {
      value = step.configuration.sections[0].sectionName;
    }
    return (
      <tr>
        <td>{rowIndex + 1}</td>
        <td>{value}</td>
        <td>
          <i>No data available</i>
        </td>
      </tr>
    );
  }
  return (
    <>
      {step.configuration.sections.map((section, index) => (
        <tr key={index}>
          <td>{rowIndex + 1}</td>
          <td>{section.sectionName}</td>
          <td>
            {section.options.map((o, optionIndex) => (
              <DamageEntry
                key={`damageEntry-${optionIndex}`}
                damagesData={damagesData}
                o={o}
              />
            ))}
          </td>
        </tr>
      ))}
    </>
  );
};

const DimensionsRow = ({
  refurbIdentifier,
  rowIndex,
  step,
}: SummaryRowProps<GradingStepDimensions>) => {
  const dimsData = useReadGradingAndRefurbState<
    Record<string, Record<string, number>>
  >(refurbIdentifier, step.stepOutputKey);
  if (!dimsData) {
    return (
      <tr>
        <td>{rowIndex + 1}</td>
        <td>Box Dimensions</td>
        <td>
          <i>No data available</i>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{rowIndex + 1}</td>
      <td>Box Dimensions</td>
      <td>
        {Object.values(dimsData).map((value: Record<string, number>, i) => {
          const dim1 = value["dim1"];
          const dim2 = value["dim2"];
          const dim3 = value["dim3"];
          if (dim1 !== undefined && dim2 !== undefined && dim3 !== undefined) {
            return (
              <p key={i}>
                <b>Box {i + 1}</b> {`${dim1}cm`} <code>x</code> {`${dim2}cm`}{" "}
                <code>x</code> {`${dim3}cm`}
              </p>
            );
          } else {
            return (
              <p key={i}>
                <b>Box {i + 1}</b> <i>Invalid data</i>
              </p>
            );
          }
        })}
      </td>
    </tr>
  );
};

const DamageEntry = ({
  o,
  damagesData,
}: {
  o: DetailedInformationSectionOption;
  damagesData: Record<string, Record<string, number | boolean>>;
}): JSX.Element => {
  if (o.optionType === "checkbox") {
    const value = damagesData[o.optionKey];

    return (
      <p>
        <b>{o.optionName}:</b> {value ? "Yes" : "No"}
      </p>
    );
  }

  if (!o.countOptions) {
    return <b>Invalid Configuration</b>;
  }

  return (
    <p>
      <b>{o.optionName}:</b>
      {o.countOptions.map((co, coIndex) => {
        if (damagesData[o.optionKey] === undefined) {
          return (
            <span key={coIndex}>
              <br />
              <i>{co.displayName}</i>: 0
            </span>
          );
        }

        return (
          <span key={coIndex}>
            <br />
            <i>{co.displayName}</i>
            {": "}
            {damagesData[o.optionKey][co.resultKey] !== undefined
              ? damagesData[o.optionKey][co.resultKey]
              : 0}
          </span>
        );
      })}
    </p>
  );
};

const SingleChoiceRow = ({
  rowIndex,
  refurbIdentifier,
  step,
}: SummaryRowProps<GradingStepChoice>) => {
  const grade = useReadGradingAndRefurbState<string>(
    refurbIdentifier,
    step.stepOutputKey
  );
  return (
    <tr>
      <td>{rowIndex + 1}</td>
      <td>{step.title}</td>
      <td>
        <span>{grade}</span>
      </td>
    </tr>
  );
};
const MultiChoiceRow = ({
  rowIndex,
  refurbIdentifier,
  step,
}: SummaryRowProps<GradingStepMultiChoice>) => {
  const choices = useReadGradingAndRefurbState<string[]>(
    refurbIdentifier,
    step.stepOutputKey
  );
  return (
    <tr>
      <td>{rowIndex + 1}</td>
      <td>Choice</td>
      <td>
        <span>{choices?.join(", ")}</span>
      </td>
    </tr>
  );
};

const FreeTextRow = ({
  rowIndex,
  refurbIdentifier,
  step,
}: SummaryRowProps<GradingStepFreeText>) => {
  const inputValue = useReadGradingAndRefurbState<string>(
    refurbIdentifier,
    step.stepOutputKey
  );
  return (
    <tr>
      <td>{rowIndex + 1}</td>
      <td>{step.title}</td>
      <td>
        <span>{inputValue}</span>
      </td>
    </tr>
  );
};

const ConfirmRow = ({
  refurbIdentifier,
  step,
  rowIndex,
}: SummaryRowProps<GradingStepConfirm>) => {
  const isConfirmed = useReadGradingAndRefurbState<string>(
    refurbIdentifier,
    step.stepOutputKey
  );

  return (
    <tr>
      <td>{rowIndex + 1}</td>
      <td>{step.description}</td>
      <td>{isConfirmed ? "Confirmed" : ""}</td>
    </tr>
  );
};

const ImageStepSummaryRow = ({
  refurbIdentifier,
  step,
  rowIndex,
}: SummaryRowProps<GradingStepImage>) => {
  const storedImage = useReadGradingAndRefurbState<string[]>(
    refurbIdentifier,
    step.stepOutputKey
  );

  return (
    <tr>
      <td>{rowIndex + 1}</td>
      <td>{step.title}</td>
      <td>
        {storedImage && storedImage.length > 0 ? (
          <b>
            {storedImage.length} image{storedImage.length > 1 ? "s" : ""}{" "}
            uploaded
          </b>
        ) : (
          <i>No image uploaded</i>
        )}
      </td>
    </tr>
  );
};
